import { useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import InventoryMovementService from '../../services/inventory/InventoryMovementService';
import RestaurantInventoryService from '../../services/restaurant/RestaurantInventoryService';
import RestaurantProductionInventoryService from '../../services/restaurant/RestaurantProductionInventoryService';
import RestaurantProductionPlannerService from '../../services/restaurant/RestaurantProductionPlannerService';
import RestaurantPurchaseOrderService from '../../services/restaurant/RestaurantPurchaseOrderService';
import { AppDispatch, RootState } from '../../store/store';
import {
  closeProductionBatch,
  openProductionBatch,
  resetProductionPlanner,
  setProductionHistory,
  setProductionHistoryLoading,
  setProductionNotes,
  setProductionOptionsLoading,
  setProductionPlannerLoading,
  setProductionProducing,
  setProductionPurchase,
  setProductionPurchases,
  setProductionQuantity,
  setProductionResult,
  setProductionSource,
  setProductionSuggestions,
} from '../../store/slices/restaurant/restaurantProductionPlannerSlice';
import type { InventoryMovementDTO } from '../../types/inventory/InventoryMovementTypes';
import type {
  RestaurantInventoryMovement,
  RestaurantLowStockAlert,
} from '../../types/restaurant/RestaurantProductionInventoryTypes';
import type {
  ProductionBatch,
  ProductionBatchFilters,
  ProductionPlanningSource,
  ProductionSuggestionFilters,
} from '../../types/restaurant/RestaurantProductionPlannerTypes';
import { validateProductionSelection } from '../../features/restaurant/productionPlanner/productionPlannerValidation';
import { RESTAURANT_INVENTORY_UPDATED_EVENT } from '../../features/restaurant/production/restaurantInventoryEvents';
import useAlerts from '../useAlerts';
import useApiErrorHandler from '../useApiErrorHandler';
import useConfirm from '../useConfirmModal';

const useRestaurantProductionPlanner = (branchId: number | '') => {
  const dispatch = useDispatch<AppDispatch>();
  const state = useSelector(
    (root: RootState) => root.restaurantProductionPlanner
  );
  const submittingRef = useRef(false);
  const { show } = useAlerts();
  const { handle } = useApiErrorHandler();
  const confirm = useConfirm();
  const validation = validateProductionSelection(
    Object.values(state.suggestionCache),
    state.quantities
  );
  const selectedPurchase = state.purchases.find(
    (item) => item.id === Number(state.purchaseId)
  );

  const loadOptions = async () => {
    if (!branchId) return;
    dispatch(setProductionOptionsLoading(true));
    try {
      const [drafts, confirmed] = await Promise.all([
        RestaurantPurchaseOrderService.list(branchId, { status: 'DRAFT', page: 0, size: 100 }),
        RestaurantPurchaseOrderService.list(branchId, { status: 'CONFIRMED', page: 0, size: 100 }),
      ]);
      const purchases = [...drafts.content, ...confirmed.content]
        .filter(
          (item, index, all) =>
            all.findIndex((candidate) => candidate.id === item.id) === index
        )
        .sort((a, b) => b.purchaseDate.localeCompare(a.purchaseDate));
      dispatch(setProductionPurchases(purchases));
    } catch (error) {
      handle(error);
    } finally {
      dispatch(setProductionOptionsLoading(false));
    }
  };

  const calculate = async (filters?: Omit<ProductionSuggestionFilters, 'source' | 'purchaseId'>) => {
    if (!branchId || state.loading) return;
    if (state.source !== 'INVENTORY' && !state.purchaseId) {
      show(
        'warning',
        'Compra requerida',
        'Seleccione una compra para calcular la producción posible.'
      );
      return;
    }
    dispatch(setProductionPlannerLoading(true));
    try {
      const response = await RestaurantProductionPlannerService.suggestions(
        branchId,
        {
          source: state.source,
          purchaseId: state.purchaseId ? Number(state.purchaseId) : null,
          page: filters?.page ?? 0,
          size: filters?.size ?? 20,
          search: filters?.search,
          categoryId: filters?.categoryId,
        }
      );
      dispatch(setProductionSuggestions(response));
    } catch (error) {
      handle(error);
    } finally {
      dispatch(setProductionPlannerLoading(false));
    }
  };

  const loadHistory = async (filters: ProductionBatchFilters = { page: 0, size: 20 }) => {
    if (!branchId) return;
    dispatch(setProductionHistoryLoading(true));
    try {
      dispatch(
        setProductionHistory(await RestaurantProductionPlannerService.history(branchId, filters))
      );
    } catch (error) {
      handle(error);
    } finally {
      dispatch(setProductionHistoryLoading(false));
    }
  };

  const enrichResult = async (batch: ProductionBatch) => {
    const itemIds = new Set(batch.items.map((item) => item.id));
    let rawMaterialMovements: RestaurantInventoryMovement[] = [];
    let productMovements: InventoryMovementDTO[] = [];
    let lowStockAlerts: RestaurantLowStockAlert[] = [];
    try {
      const [rawResponse, lowStockResponse, ...productResponses] =
        await Promise.all([
          RestaurantProductionInventoryService.listMovements(batch.branchId),
          RestaurantInventoryService.list(batch.branchId, {
            activeOnly: true,
            lowStockOnly: true,
          }),
          ...batch.items.map((item) =>
            InventoryMovementService.getFiltered(
              {
                branchId: batch.branchId,
                productId: item.productId,
                movementType: 'ENTRY',
              },
              0,
              50
            )
          ),
        ]);
      rawMaterialMovements = rawResponse.content.filter(
        (movement) =>
          movement.referenceType === 'RESTAURANT_PRODUCTION_BATCH_ITEM' &&
          movement.referenceId != null &&
          itemIds.has(movement.referenceId)
      );
      productMovements = productResponses
        .flatMap((page) => page?.content ?? [])
        .filter(
          (movement: InventoryMovementDTO) => movement.referenceId === batch.id
        );
      lowStockAlerts = lowStockResponse.content.map((item) => ({
        rawMaterialId: item.rawMaterialId,
        rawMaterialCode: item.rawMaterialCode,
        rawMaterialName: item.rawMaterialName,
        currentStock: item.currentStock,
        minimumStock: item.minimumStock,
        unitCode: item.unitCode,
      }));
    } catch {
      /* El lote ya fue confirmado; la trazabilidad auxiliar no debe convertir éxito en error. */
    }
    return { batch, rawMaterialMovements, productMovements, lowStockAlerts };
  };

  const executeProduction = async () => {
    if (
      !branchId ||
      state.producing ||
      submittingRef.current ||
      !validation.valid
    )
      return;
    submittingRef.current = true;
    dispatch(setProductionProducing(true));
    try {
      const response = await RestaurantProductionPlannerService.produce(
        branchId,
        {
          source: state.source,
          purchaseId:
            state.source === 'INVENTORY' ? null : Number(state.purchaseId),
          items: validation.selectedItems,
          notes: state.notes.trim() || null,
        }
      );
      const result = await enrichResult(response.data);
      window.dispatchEvent(new Event(RESTAURANT_INVENTORY_UPDATED_EVENT));
      await Promise.all([calculate(), loadHistory()]);
      dispatch(setProductionResult(result));
      show(
        response.type || 'success',
        response.title || 'Producción completada',
        response.message || 'El lote fue producido correctamente.'
      );
    } catch (error) {
      handle(error);
    } finally {
      submittingRef.current = false;
      dispatch(setProductionProducing(false));
    }
  };

  const requestProduction = () => {
    if (!validation.valid) {
      show(
        'warning',
        'Revise la planificación',
        validation.message || 'La selección no es válida.'
      );
      return;
    }
    if (
      state.source !== 'INVENTORY' &&
      selectedPurchase?.status !== 'CONFIRMED'
    ) {
      show(
        'warning',
        'Compra pendiente',
        'Puede planificar con esta compra, pero debe confirmarla antes de producir.'
      );
      return;
    }
    confirm({
      title: 'Confirmar lote de producción',
      message: `Se producirán ${validation.selectedItems.length} receta(s) por un costo estimado de ${validation.estimatedTotalCost}. Esta acción modificará ambos inventarios.`,
      icon: 'warning',
      confirmText: 'Sí, producir',
      cancelText: 'Revisar',
      onConfirm: executeProduction,
    });
  };

  const viewBatch = async (batch: ProductionBatch) => {
    if (!branchId) return;
    try {
      dispatch(
        openProductionBatch(
          (
            await RestaurantProductionPlannerService.getBatch(
              branchId,
              batch.id
            )
          ).data
        )
      );
    } catch (error) {
      handle(error);
    }
  };

  return {
    ...state,
    validation,
    selectedPurchase,
    loadOptions,
    calculate,
    loadHistory,
    requestProduction,
    viewBatch,
    changeSource: (source: ProductionPlanningSource) =>
      dispatch(setProductionSource(source)),
    changePurchase: (purchaseId: number | '') =>
      dispatch(setProductionPurchase(purchaseId)),
    changeQuantity: (recipeId: number, quantity: string) =>
      dispatch(setProductionQuantity({ recipeId, quantity })),
    changeNotes: (notes: string) => dispatch(setProductionNotes(notes)),
    clearResult: () => dispatch(setProductionResult(null)),
    closeBatch: () => dispatch(closeProductionBatch()),
    reset: () => dispatch(resetProductionPlanner()),
  };
};

export default useRestaurantProductionPlanner;
